import cn from 'classnames';
import { ReactNode } from 'react';
import { mixColors, hexToRgb, Neutral100, rgbToHex } from '@src/utils/colors';
import { toPrettyNumber } from '@src/utils/performance-utils';
import { ComparedBrand, FramingSummary } from '../../alexa-visibility-competitive-utils';
import { TargetCitationSummary } from '../../alexa-visibility-citation-utils';
import { CitationMedal } from '../citation-comparison/CitationMedal';

const percentOf = (count: number, total: number): string =>
  `${total > 0 ? ((count / total) * 100).toFixed(0) : 0}%`;

interface StatCardProps {
  tone: 'total' | 'favorable' | 'neutral' | 'unfavorable';
  label: string;
  value: number;
  line: string;
  note: string;
}

const StatCard = ({ tone, label, value, line, note }: StatCardProps) => (
  <div className={cn('aars-comp-stat', `is-${tone}`)}>
    <div className='aars-comp-stat-label'>{label}</div>
    <div className='aars-comp-stat-value'>{toPrettyNumber(value)}</div>
    <div className='aars-comp-stat-line'>{line}</div>
    <div className='aars-comp-stat-note'>{note}</div>
  </div>
);

interface BarRowProps {
  label: ReactNode;
  count: number;
  total: number;
  color?: string;
  className?: string;
}

const BarRow = ({ label, count, total, color, className }: BarRowProps) => (
  <div className={cn('aars-comp-bar-row', className)}>
    <div className='aars-comp-bar-label'>{label}</div>
    <div className='aars-comp-bar-track'>
      <div
        className='aars-comp-bar-fill'
        style={{ width: `${total > 0 ? (count / total) * 100 : 0}%`, backgroundColor: color }}
      />
    </div>
    <div className='aars-comp-bar-value'>
      <strong>{toPrettyNumber(count)}</strong> ({percentOf(count, total)})
    </div>
  </div>
);

interface CompetitiveSummaryProps {
  targetBrand: string;
  framing: FramingSummary;
  rank: TargetCitationSummary;
  comparedAgainst: ComparedBrand[];
  getBrandColor: (brand: string) => string;
}

export const CompetitiveSummary = ({
  targetBrand,
  framing,
  rank,
  comparedAgainst,
  getBrandColor
}: CompetitiveSummaryProps) => (
  <div className='aars-comp-summary'>
    <div className='aars-comp-stat-grid'>
      <StatCard
        tone='total'
        label='Competitive citations'
        value={framing.total}
        line='total comparisons'
        note='across all runs'
      />
      <StatCard
        tone='favorable'
        label='Favorable framing'
        value={framing.favorable}
        line={`${percentOf(framing.favorable, framing.total)} of comparisons`}
        note={`${targetBrand} wins`}
      />
      <StatCard
        tone='neutral'
        label='Neutral framing'
        value={framing.neutral}
        line={`${percentOf(framing.neutral, framing.total)} of comparisons`}
        note='no clear winner'
      />
      <StatCard
        tone='unfavorable'
        label='Unfavorable framing'
        value={framing.unfavorable}
        line={`${percentOf(framing.unfavorable, framing.total)} of comparisons`}
        note='competitor wins'
      />
    </div>
    <div className='aars-comp-detail-grid'>
      <div className='aars-cc-card'>
        <div className='aars-cc-card-title'>{targetBrand} rank when compared</div>
        <div className='aars-cc-card-description'>
          Where {targetBrand} lands in the recommendation order when Alexa makes a direct comparison
        </div>
        <div className='aars-comp-bars'>
          <BarRow
            label={
              <>
                <CitationMedal place={1} /> Ranked #1
              </>
            }
            count={rank.rank1}
            total={rank.total}
            className='is-rank-1'
          />
          <BarRow
            label={
              <>
                <CitationMedal place={2} /> Ranked #2
              </>
            }
            count={rank.rank2}
            total={rank.total}
            className='is-rank-2'
          />
          <BarRow
            label={
              <>
                <CitationMedal place={3} /> Ranked #3
              </>
            }
            count={rank.rank3}
            total={rank.total}
            className='is-rank-3'
          />
          <BarRow
            label='Not ranked'
            count={rank.notRanked}
            total={rank.total}
            className='is-not-ranked'
          />
        </div>
      </div>
      <div className='aars-cc-card'>
        <div className='aars-cc-card-title'>Most compared against</div>
        <div className='aars-cc-card-description'>
          Brands Alexa mentions alongside {targetBrand} in competitive comparisons
        </div>
        <div className='aars-comp-bars'>
          {comparedAgainst.map(({ brand, count }) => {
            const color = getBrandColor(brand);
            return (
              <BarRow
                key={brand}
                label={
                  <span
                    className='aars-comp-brand-pill'
                    style={{
                      color,
                      backgroundColor: rgbToHex(
                        mixColors(hexToRgb(Neutral100), hexToRgb(color), 0.14)
                      )
                    }}
                  >
                    {brand}
                  </span>
                }
                count={count}
                total={framing.total}
                color={color}
              />
            );
          })}
          {comparedAgainst.length === 0 && (
            <span className='aars-cc-empty'>No other brands mentioned in comparisons</span>
          )}
        </div>
      </div>
    </div>
  </div>
);
